import { apiClient } from './client';
import type { RequestOptions } from '@/types/api';
import type {
  NginxConfigDetail,
  NginxConfigListResponse,
  NginxConfigFilters,
  GenerateNginxConfigRequest,
  GenerateNginxConfigResponse,
  ReloadNginxRequest,
  ReloadNginxResponse,
} from '@/types/nginx';

/**
 * NGINX API functions for the CDN Manager
 */
export const nginxApi = {
  /**
   * Fetch all nginx configurations with optional filters
   */
  async getAll(filters?: NginxConfigFilters, options?: RequestOptions): Promise<NginxConfigListResponse> {
    const params: Record<string, string> = {};

    if (filters?.node_id) {
      params.node_id = filters.node_id;
    }
    if (filters?.domain_id) {
      params.domain_id = filters.domain_id;
    }
    if (filters?.is_active !== undefined) {
      params.is_active = String(filters.is_active);
    }
    if (filters?.last_reload_status) {
      params.last_reload_status = filters.last_reload_status;
    }

    const response = await apiClient.get<NginxConfigListResponse>('/nginx/configs', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Fetch a single nginx config by ID with full content
   */
  async getById(id: string, options?: RequestOptions): Promise<NginxConfigDetail> {
    const response = await apiClient.get<NginxConfigDetail>(`/nginx/configs/${id}`, {
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Generate a new nginx config for a domain
   */
  async generate(data: GenerateNginxConfigRequest): Promise<GenerateNginxConfigResponse> {
    const response = await apiClient.post<GenerateNginxConfigResponse>('/nginx/generate', data);
    return response.data;
  },

  /**
   * Reload nginx on a node (zero-downtime)
   */
  async reload(data: ReloadNginxRequest): Promise<ReloadNginxResponse> {
    const response = await apiClient.post<ReloadNginxResponse>('/nginx/reload', data);
    return response.data;
  },

  /**
   * Activate an nginx config and reload nginx
   */
  async activate(id: string): Promise<ReloadNginxResponse> {
    const response = await apiClient.post<ReloadNginxResponse>(`/nginx/configs/${id}/activate`);
    return response.data;
  },

  /**
   * Regenerate an existing nginx config
   */
  async regenerate(id: string): Promise<GenerateNginxConfigResponse> {
    const response = await apiClient.post<GenerateNginxConfigResponse>(`/nginx/configs/${id}/regenerate`);
    return response.data;
  },

  /**
   * Delete an nginx config
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/nginx/configs/${id}`);
  },
};
